/**
 * The chosen Support at home plan, as the plan page and Home read it (rebuild M2, 19 Oct 2026).
 *
 * When a plan is chosen — tested or built by hand — its card is stored on the record: the figures the
 * card showed, the figures the support alone gave at the time, the plan in words, what happens to each
 * pension account it draws on, and its risk kinds. The plan page shows and explains the plan from
 * this, so what the family reads there is exactly what they chose, with no re-run in between.
 */
import type { SahFigures } from './SahPlanDialogs.tsx';
import type { RiskKind } from '../../strategy/strategyRisks.ts';
import { HOME_CARE_STRATEGIES_KEY } from '../../strategy/careCompanionStrategyEvents.ts';

export interface SahPlanCardRecord {
    plan: SahFigures;
    baseline: SahFigures | null;
    words: { title: string; sub: string; treatment: string[] };
    drawLines: Array<{ lead: string; body: string }>;
    riskKinds: RiskKind[];
    horizonYears: number;
    /** 'engine' — one of the tested plans; 'manual' — built by hand. */
    origin: 'engine' | 'manual';
    /** The engine candidate's config, so "Customise this plan" can start from it. */
    engineConfig?: unknown;
    /** The money the plan moves (the compare table's last column). */
    moved?: number;
}

/** The card, light enough to save: the figure pop-over's row lists are not kept (413 on save, 9 Oct 2026). */
export function sahCardRecord(card: SahPlanCardRecord): SahPlanCardRecord {
    const slim = (f: SahFigures | null): SahFigures | null => (f ? { ...f, pensionRows: [], cashFlowBreakdown: null } : null);
    return { ...card, plan: slim(card.plan) as SahFigures, baseline: slim(card.baseline) };
}

export interface ChosenSahPlan {
    id: string;
    name?: string;
    displayName?: string;
    source?: string;
    chosen?: boolean;
    createdAt?: string;
    soaSnapshot?: unknown;
    inputs?: {
        card?: SahPlanCardRecord;
        servicePlan?: { client?: Record<string, unknown>; partner?: Record<string, unknown> } | null;
        servicePlanFingerprint?: string;
        legacyYears?: number;
        [key: string]: unknown;
    };
    outputs?: Record<string, unknown>;
    [key: string]: unknown;
}

/** The chosen Support at home plan on the file, if one is chosen and carries its card. */
export function chosenSahPlanOf(list: ReadonlyArray<Record<string, unknown>> | null | undefined): ChosenSahPlan | null {
    const hit = (list || []).find((s) => s?.type === 'home_care' && s.chosen === true && (s.inputs as { card?: unknown } | undefined)?.card);
    return (hit as ChosenSahPlan | undefined) ?? null;
}

/** The same, read from the session's working copy. */
export function chosenSahPlanFromSession(): ChosenSahPlan | null {
    try {
        const raw = typeof window !== 'undefined' ? window.sessionStorage.getItem(HOME_CARE_STRATEGIES_KEY) : null;
        const list = raw ? JSON.parse(raw) : [];
        return Array.isArray(list) ? chosenSahPlanOf(list) : null;
    } catch {
        return null;
    }
}

/** Session key: Home's "Start a new model" — open on an empty step 1 and ask the setup questions again (one-shot). */
export const SAH_START_FRESH_KEY = 'careCompanionSahStartFresh';

/** Session key: what the plan page asked Optimise to do when it opens (one-shot). */
export const SAH_HUB_INTENT_KEY = 'careCompanionSahHubIntent';
export type SahHubIntent = { kind: 'build'; config: unknown; name: string } | { kind: 'build-new' } | { kind: 'document' } | { kind: 'compare' } | { kind: 'yearByYear' };

export function setSahHubIntent(intent: SahHubIntent | null): void {
    try {
        if (intent) window.sessionStorage.setItem(SAH_HUB_INTENT_KEY, JSON.stringify(intent));
        else window.sessionStorage.removeItem(SAH_HUB_INTENT_KEY);
    } catch { /* storage unavailable */ }
}

/** Read and clear. */
export function takeSahHubIntent(): SahHubIntent | null {
    try {
        const raw = window.sessionStorage.getItem(SAH_HUB_INTENT_KEY);
        window.sessionStorage.removeItem(SAH_HUB_INTENT_KEY);
        return raw ? (JSON.parse(raw) as SahHubIntent) : null;
    } catch {
        return null;
    }
}
